
// components/Sidebar.tsx
'use client';

import { useState, useRef } from 'react';
import { PlusIcon, type PlusIconHandle } from '@animateicons/react/lucide';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useTheme } from '@/theme/ThemeContext';
import {
  Home,
  BuildingComplex,
  ClipboardMinus,
  Users,
  Settings,
  Plus,
  ListFilter,
  Clock,
  Star,
  BookUser,
  Presentation,
  Menu,
  X, UserLock
} from 'lucide-react';

import { useIconHover, type IconHandle } from "@animateicons/react";
import {
  HouseIcon,

  UsersIcon,
  PresentationIcon,
  Building2Icon,
  UserLockIcon,
  SettingsIcon,
} from "@animateicons/react/lucide";

import type { ComponentType, Ref } from "react";

type IconProps = {
  size?: number;
  ref?: Ref<IconHandle>;
};

type NavItemProps = {
  label: string;
  href: string;
  icon: ComponentType<IconProps>;
  active: boolean;
  onNavigate: () => void;
};

export function NavItem({
  label,
  href,
  icon: Icon,
  active,
  onNavigate,
}: NavItemProps) {

  const { ref, triggerProps } = useIconHover({
    trigger: "hover",
  });

  return (
    <Link
      href={href}
      {...triggerProps}
      onClick={onNavigate}
      className={`
        flex items-center gap-3.5 px-4 py-3 rounded-full
        text-sm font-medium transition-all duration-200
        ${active
          ? "bg-[#c2e7ff] text-[#001d35] font-semibold shadow-xs"
          : "text-white/90 hover:bg-white/10 hover:text-white"
        }
      `}
    >
      <span className={active ? "text-[#001d35]" : "text-white/80"}>
        <Icon ref={ref} size={20} />
      </span>

      <span>{label}</span>
    </Link>
  );
}



const navItems = [
  { label: "Inicio", href: "/app/main", icon: HouseIcon },
  {
    label: "Trabajadores",
    href: "/app/trabajadores",
    icon: UsersIcon,
  },
  { label: "Cursos", href: "/app/cursos", icon: PresentationIcon },
  { label: "Empresas", href: "/app/empresas", icon: Building2Icon },
  { label: "Instructores", href: "/instructores", icon: UserLockIcon },
  { label: "Ajustes", href: "/app/ajustes", icon: SettingsIcon },
];


// function NavItem({icon}:{icon:}){

// }

export default function Sidebar() {
  const { theme, toggleTheme } = useTheme();
  const plus = useRef<PlusIconHandle>(null);

  const [isOpen, setIsOpen] = useState(false);
  const pathname = usePathname();

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  return (
    <>
      {/* Botón móvil */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed top-4 left-4 z-50 p-2 bg-white rounded-xl shadow-md md:hidden text-slate-600"
      >
        <Menu size={22} />
      </button>

      {/* Overlay móvil */}
      {isOpen && (
        <div onClick={() => setIsOpen(false)} className="fixed inset-0 bg-black/40 backdrop-blur-xs z-40 md:hidden" />
      )}

      {/* Sidebar estilo Google Drive (Fondo azul vibrante con esquinas redondeadas) top-0 left-0 */}
      <aside className={`
        fixed left-0 h-full w-64 ${theme === 'light' ? 'bg-[#1a73e8]' : 'bg-[#1e3a8a]'} text-white z-50
        flex flex-col p-4 transition-transform duration-300 ease-in-out shadow-xl md:shadow-none
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0 rounded-tr-4xl
      `}>
        {/* Logo / Header del Sidebar */}
        <div className="flex items-center justify-between mb-6 px-2">
          <div className="flex items-center gap-3">
            <div className="bg-white p-2 rounded-xl text-[#1a73e8] shadow-sm">
              <ListFilter size={24} />
            </div>
            <span className="text-lg font-bold tracking-wide">Contenido</span>
          </div>
          <button onClick={() => setIsOpen(false)} className="md:hidden text-white/80 hover:text-white">
            <X size={22} />
          </button>
        </div>

        {/* Botón de Acción Principal (Estilo "Upload New File") */}
        <div className="mb-6">
          <button className="flex items-center gap-3 bg-white text-[#1a73e8] font-medium px-5 py-3 rounded-full shadow-md hover:shadow-lg hover:bg-slate-50 transition-all w-full justify-center"
            onMouseEnter={() => plus.current?.startAnimation()}
            onMouseLeave={() => plus.current?.stopAnimation()}
          >
            {/* <Plus size={20} /> */}

            <PlusIcon
              size={20}
              duration={1}
              // isAnimated={true}
              ref={plus}
            />
            <span>Nueva DC3</span>
          </button>
        </div>

        {/* Navegación Principal */}
        <nav className="flex-1 space-y-1 overflow-y-auto pr-1">
          {/* <nav className="flex-1 space-y-1 overflow-y-auto pr-1"> */}
          {navItems.map((item) => (
            <NavItem
              key={item.href}
              label={item.label}
              href={item.href}
              icon={item.icon}
              active={isActive(item.href)}
              onNavigate={() => setIsOpen(false)}
            />
          ))}
        </nav>

        {/* Widget de Almacenamiento Inferior (Inspirado en el boceto) */}
        {/* <div className="mt-auto pt-4 border-t border-white/20 px-2">
          <div className="flex items-center gap-3 mb-2 text-white/90">
            <HardDrive size={18} />
            <span className="text-xs font-medium">Almacenamiento</span>
          </div>
          <div className="w-full bg-black/20 h-2 rounded-full overflow-hidden mb-2">
            <div className="bg-white h-full w-[65%]" />
          </div>
          <p className="text-[11px] text-white/70">65 GB de 1 TB usado</p>
        </div> */}
      </aside>
    </>
  );
}
